import { createIsomorphicFn } from '@tanstack/react-start';
import { apiClient } from '@repo/shared/client';
import type { ApiContract, ApiRequestOptions, InferResponse } from '@repo/shared/common';

/**
 * Builds an **isomorphic** fetcher for a read contract — the one place the
 * server/client split for loader data lives.
 *
 * - On the **server** (SSR, or a server-executed preload) it calls the handler
 *   core directly in-process via {@link resolveContractOnServer} — no HTTP, no
 *   Worker self-fetch, request cookies available through `getRequest()`.
 * - On the **client** (post-hydration navigation) it calls `apiClient(contract)`
 *   — a real same-origin request the browser attaches the session cookie to.
 *
 * We deliberately use API routes + this isomorphic seam instead of TanStack
 * server functions: the Zod contract is the explicit, rename-safe interface, and
 * there's no generated RPC binding to break.
 *
 * Implementation notes for anyone editing this:
 * - The `createIsomorphicFn().server().client()` chain MUST stay a single
 *   expression. Splitting it across variables defeats the Start compiler's
 *   detection (TanStack/router#6206).
 * - The server module is pulled in with a **dynamic `import()`** inside
 *   `.server()`, never a top-level import. Two environments demand this: the
 *   Start compiler strips `.server()` from the browser build (so the server graph
 *   — `cloudflare:workers`, `pg` — never reaches `dist/client`), AND vitest/Node
 *   run WITHOUT the compiler, where a *static* import would eagerly evaluate
 *   `db.server` → `cloudflare:workers` and crash any test that imports a module
 *   reaching this file. The dynamic import only executes when the (transformed)
 *   server branch actually runs, so neither environment ever loads it.
 * - Untransformed (e.g. in a vitest unit test, with no Start plugin), the stub
 *   is a no-op. Don't unit-test the seam here — test the data cores and the
 *   `apiClient` path directly; verify the seam via build/E2E. (Server names still
 *   appear in `*.js.map` source maps — grep executed `.js`, never `.map`.)
 */
export function createContractFetcher<T extends ApiContract & { readonly method: 'GET' }>(
  contract: T,
): (options?: ApiRequestOptions<T>) => Promise<InferResponse<T>> {
  return createIsomorphicFn()
    .server(async (options?: ApiRequestOptions<T>) => {
      const { resolveContractOnServer } = await import('./api-dispatch.server');
      return resolveContractOnServer(contract, options);
    })
    .client((options?: ApiRequestOptions<T>) => apiClient(contract, options));
}
